import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Rightchat from "../components/Rightchat";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import {
  getPublicFormsApi,
  getAssignedFormsApi,
} from "../api/TaskActions";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

interface AssignedForm {
  task_id: number;
  task_title: string;
  task_description?: string | null;
  deadline?: string | null;
  allowed_responses?: number;
  created_by?: number;
  audience_type?: string;
  task_status?: "active" | "closed";
  submission_status?:
    | "pending"
    | "submitted"
    | "partially_completed";
  submitted_responses?: number;
  total_fields?: number;
  latest_response_id?: number | null;
  can_submit?: number;
  client_id?: number | null;
  client_name?: string | null;
  logo_image?: string | null;
  unique_slug?: string | null;
}

interface PublicFormListItem {
  task_id: number;
  task_title: string;
  task_description?: string | null;
  deadline?: string | null;
  allowed_responses?: number;
  audience_type: "public";
  public_form_slug: string;
  status?: string | null;
  created_at?: string | null;
  created_by?: number | null;
  client_id?: number | null;
  client_name?: string | null;
  logo_image?: string | null;
  unique_slug?: string | null;
  total_fields?: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
}

const getUploadUrl = (file?: string | null) => {
  if (!file || !String(file).trim()) {
    return "/assets/images/user.png";
  }

  const value = String(file).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API_URL}${value}`;
  }

  return `${API_URL}/${value}`;
};

const formatDeadline = (deadline?: string | null) => {
  if (!deadline) {
    return "No deadline";
  }

  const date = new Date(deadline);

  if (Number.isNaN(date.getTime())) {
    return "No deadline";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AssignedForms: React.FC = () => {
  const navigate = useNavigate();

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("user") || "{}",
      );
    } catch {
      return {};
    }
  }, []);

  const userId = Number(currentUser?.user_id || 0);

  const [forms, setForms] = useState<AssignedForm[]>([]);
  const [publicForms, setPublicForms] = useState<PublicFormListItem[]>([]);
  const [publicFormsLoading, setPublicFormsLoading] = useState(true);
  const [publicFormsError, setPublicFormsError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [taskStatus, setTaskStatus] = useState("all");
  const [submissionStatus, setSubmissionStatus] =
    useState("all");
  const [page, setPage] = useState(1);

  const [pagination, setPagination] =
    useState<Pagination>({
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    });

  useEffect(() => {
    const controller = new AbortController();

    const fetchPublicForms = async () => {
      try {
        setPublicFormsLoading(true);
        setPublicFormsError("");

        const result = await getPublicFormsApi(
          {
            search: searchTerm.trim(),
            status: taskStatus,
            page: 1,
            limit: 50,
          },
          controller.signal,
        );

        if (!result?.success) {
          throw new Error(
            result?.message ||
              "Failed to fetch public forms",
          );
        }

        setPublicForms(
          Array.isArray(result?.data)
            ? result.data
            : [],
        );
      } catch (err: any) {
        if (
          err?.name === "CanceledError" ||
          err?.code === "ERR_CANCELED"
        ) {
          return;
        }

        console.error("Public forms error:", err);

        setPublicForms([]);
        setPublicFormsError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to fetch public forms",
        );
      } finally {
        setPublicFormsLoading(false);
      }
    };

    const timer = window.setTimeout(
      fetchPublicForms,
      250,
    );

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [searchTerm, taskStatus]);

  useEffect(() => {
    // Guest users are allowed to open the Forms page.
    // Assigned forms are loaded only when a valid user_id exists.
    if (!userId) {
      setForms([]);
      setLoading(false);
      setError("");
      setPagination({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      });
      return;
    }

    const fetchAssignedForms = async () => {
      try {
        setLoading(true);
        setError("");

        const result = await getAssignedFormsApi({
          user_id: userId,
          search: searchTerm.trim(),
          task_status: taskStatus,
          submission_status: submissionStatus,
          page,
          limit: 10,
        });

        if (!result?.success) {
          throw new Error(
            result?.message ||
              "Failed to fetch assigned forms",
          );
        }

        setForms(
          Array.isArray(result?.data)
            ? result.data
            : [],
        );

        setPagination(
          result?.pagination || {
            total: 0,
            page,
            limit: 10,
            totalPages: 0,
          },
        );
      } catch (err: any) {
        console.error("Assigned forms error:", err);

        setForms([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to fetch assigned forms",
        );
      } finally {
        setLoading(false);
      }
    };

    const timer = window.setTimeout(
      fetchAssignedForms,
      300,
    );

    return () => window.clearTimeout(timer);
  }, [
    userId,
    searchTerm,
    taskStatus,
    submissionStatus,
    page,
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, taskStatus, submissionStatus]);

  const handleOpenPublicForm = (
    form: PublicFormListItem,
  ) => {
    if (!form.public_form_slug) {
      return;
    }

    navigate(
      `/public-form/${encodeURIComponent(
        form.public_form_slug,
      )}`,
    );
  };

  const handleOpenForm = (form: AssignedForm) => {
    if (
      Number(form.can_submit) !== 1 &&
      form.submission_status === "submitted"
    ) {
      navigate(`/forms/${form.task_id}/response`);
      return;
    }

    navigate(`/forms/${form.task_id}`);
  };

  const getStatusLabel = (form: AssignedForm) => {
    if (form.submission_status === "submitted") {
      return "Submitted";
    }

    if (
      form.submission_status ===
      "partially_completed"
    ) {
      return "In Progress";
    }

    if (form.task_status === "closed") {
      return "Closed";
    }

    return "Pending";
  };

  const getStatusClass = (form: AssignedForm) => {
    if (form.submission_status === "submitted") {
      return "assigned-form-status submitted";
    }

    if (
      form.submission_status ===
      "partially_completed"
    ) {
      return "assigned-form-status progress";
    }

    if (form.task_status === "closed") {
      return "assigned-form-status closed";
    }

    return "assigned-form-status pending";
  };

  const activeCount = forms.filter(
    (form) => form.task_status === "active",
  ).length;

  const pendingCount = forms.filter(
    (form) => form.submission_status === "pending",
  ).length;

  const submittedCount = forms.filter(
    (form) =>
      form.submission_status === "submitted",
  ).length;

  return (
    <Fragment>
      <Header />
      <Leftnav />
      {userId > 0 && <Rightchat />}

      <div
        className={`main-content assigned-forms-main ${
          userId > 0 ? "right-chat-active" : ""
        }`}
      >
        <div className="middle-sidebar-bottom assigned-forms-middle">
          <div className="middle-sidebar-left assigned-forms-left">
            <div className="assigned-forms-wrapper">
              <div className="assigned-forms-header">
                <div className="assigned-forms-header-top">
                  <div className="assigned-forms-heading-wrap">
                    <span className="assigned-forms-badge">
                      Bharat Sangam Forms
                    </span>

                    <h2 className="assigned-forms-title">
                      Forms
                    </h2>

                    <p className="assigned-forms-subtitle">
                      {userId > 0
                        ? "Browse public forms and manage forms assigned to your member account."
                        : "Browse and submit public forms from Bharat Sangam clubs and communities."}
                    </p>
                  </div>

                  <div className="assigned-forms-count-box">
                    <strong>
                      {publicForms.length +
                        (userId > 0 ? pagination.total : 0)}
                    </strong>
                    <span>Forms</span>
                  </div>
                </div>

                {userId > 0 && (
                  <>
                <div className="assigned-forms-summary">
                  <div className="assigned-summary-item">
                    <div className="assigned-summary-icon active">
                      <i className="feather-file-text" />
                    </div>

                    <div>
                      <strong>{activeCount}</strong>
                      <span>Active</span>
                    </div>
                  </div>

                  <div className="assigned-summary-item">
                    <div className="assigned-summary-icon pending">
                      <i className="feather-clock" />
                    </div>

                    <div>
                      <strong>{pendingCount}</strong>
                      <span>Pending</span>
                    </div>
                  </div>

                  <div className="assigned-summary-item">
                    <div className="assigned-summary-icon submitted">
                      <i className="feather-check-circle" />
                    </div>

                    <div>
                      <strong>{submittedCount}</strong>
                      <span>Submitted</span>
                    </div>
                  </div>
                </div>
                  </>
                )}

                <div className="assigned-forms-filter-row">
                  <div className="assigned-form-select">
                    <select
                      value={taskStatus}
                      onChange={(event) =>
                        setTaskStatus(event.target.value)
                      }
                    >
                      <option value="all">All Forms</option>
                      <option value="active">
                        Active Forms
                      </option>
                      <option value="closed">
                        Closed Forms
                      </option>
                    </select>

                    <i className="feather-chevron-down" />
                  </div>

                  {userId > 0 && (
                  <div className="assigned-form-select">
                    <select
                      value={submissionStatus}
                      onChange={(event) =>
                        setSubmissionStatus(
                          event.target.value,
                        )
                      }
                    >
                      <option value="all">All Status</option>
                      <option value="pending">
                        Pending
                      </option>
                      <option value="submitted">
                        Submitted
                      </option>
                      <option value="partially_completed">
                        In Progress
                      </option>
                    </select>

                    <i className="feather-chevron-down" />
                  </div>
                  )}

                  <div className="assigned-form-search">
                    <i className="feather-search" />

                    <input
                      type="text"
                      value={searchTerm}
                      placeholder="Search forms or clubs..."
                      onChange={(event) =>
                        setSearchTerm(event.target.value)
                      }
                    />

                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        aria-label="Clear search"
                      >
                        <i className="feather-x" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <section className="public-forms-section">
                {publicFormsLoading ? (
                  <div className="assigned-forms-loader public-forms-state">
                    <div className="spinner-border text-primary" />
                    <p>Loading public forms...</p>
                  </div>
                ) : publicFormsError ? (
                  <div className="assigned-forms-error public-forms-state">
                    <div className="assigned-empty-icon error">
                      <i className="feather-alert-circle" />
                    </div>
                    <h4>Unable to Load Public Forms</h4>
                    <p>{publicFormsError}</p>
                  </div>
                ) : publicForms.length > 0 ? (
                  <div className="assigned-forms-list">
                    {publicForms.map((form) => (
                      <div
                        className="assigned-form-card public-form-card"
                        key={`public-${form.task_id}`}
                      >
                        <div className="assigned-form-side">
                          <div className="assigned-form-club-logo">
                            <img
                              src={getUploadUrl(form.logo_image)}
                              alt={form.client_name || "Club"}
                              onError={(event) => {
                                event.currentTarget.src =
                                  "/assets/images/user.png";
                              }}
                            />
                          </div>

                          <div className="assigned-form-number">
                            #{form.task_id}
                          </div>
                        </div>

                        <div className="assigned-form-body">
                          <div className="assigned-form-card-top">
                            <div className="assigned-form-heading">
                              <div className="assigned-form-club-name">
                                <i className="feather-globe" />
                                <span>
                                  {form.client_name ||
                                    "Bharat Sangam"}
                                </span>
                              </div>

                              <h3>
                                {form.task_title ||
                                  "Untitled Form"}
                              </h3>
                            </div>

                            <span className="public-form-badge">
                              Public
                            </span>
                          </div>

                          <p className="assigned-form-description">
                            {form.task_description ||
                              "Public form available for submission."}
                          </p>

                          <div className="assigned-form-meta-grid">
                            <div className="assigned-form-meta">
                              <span className="assigned-meta-icon deadline">
                                <i className="feather-calendar" />
                              </span>
                              <div>
                                <label>Deadline</label>
                                <strong>
                                  {formatDeadline(form.deadline)}
                                </strong>
                              </div>
                            </div>

                            <div className="assigned-form-meta">
                              <span className="assigned-meta-icon fields">
                                <i className="feather-list" />
                              </span>
                              <div>
                                <label>Form Fields</label>
                                <strong>
                                  {Number(form.total_fields || 0)} Fields
                                </strong>
                              </div>
                            </div>

                            <div className="assigned-form-meta">
                              <span className="assigned-meta-icon responses">
                                <i className="feather-link" />
                              </span>
                              <div>
                                <label>Access</label>
                                <strong>No login required</strong>
                              </div>
                            </div>
                          </div>

                          <div className="assigned-form-actions">
                            <div className="assigned-form-access-note public-access-note">
                              <i className="feather-globe" />
                              <span>Available to everyone</span>
                            </div>

                            <button
                              type="button"
                              className="assigned-form-open-btn"
                              onClick={() =>
                                handleOpenPublicForm(form)
                              }
                            >
                              Open Form
                              <i className="feather-arrow-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="assigned-forms-empty public-forms-state">
                    <div className="assigned-empty-icon">
                      <i className="feather-globe" />
                    </div>
                    <h4>No Public Forms Found</h4>
                    <p>
                      There are currently no active public forms matching your filters.
                    </p>
                  </div>
                )}
              </section>

              {userId > 0 && (
                <section className="assigned-member-section">
                  <div className="forms-section-heading">
                    <div>
                      <span className="forms-section-kicker">
                        Member access
                      </span>
                      <h3>My Assigned Forms</h3>
                      <p>
                        Forms assigned through your club membership, groups, or designation.
                      </p>
                    </div>

                    <span className="forms-section-count">
                      {pagination.total}
                    </span>
                  </div>
                </section>
              )}

              {!userId ? (
                <div className="guest-assigned-login-card">
                  <div className="guest-assigned-login-content">
                  <div className="guest-assigned-login-icon">
                    <i className="feather-lock" />
                  </div>
                  <div>
                    <strong>Want to see forms assigned to you?</strong>
                    <p>
                      Login to view member-only forms assigned through your clubs,
                      groups, or designation.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                  >
                    Login
                  </button>
                </div>
                </div>
              ) : loading ? (
                <div className="assigned-forms-loader">
                  <div className="spinner-border text-primary" />
                  <p>Loading assigned forms...</p>
                </div>
              ) : error ? (
                <div className="assigned-forms-error">
                  <div className="assigned-empty-icon error">
                    <i className="feather-alert-circle" />
                  </div>

                  <h4>Unable to Load Forms</h4>
                  <p>{error}</p>
                </div>
              ) : forms.length > 0 ? (
                <>
                  <div className="assigned-forms-list">
                    {forms.map((form) => (
                      <div
                        className="assigned-form-card"
                        key={form.task_id}
                      >
                        <div className="assigned-form-side">
                          <div className="assigned-form-club-logo">
                            <img
                              src={getUploadUrl(
                                form.logo_image,
                              )}
                              alt={
                                form.client_name || "Club"
                              }
                              onError={(event) => {
                                event.currentTarget.src =
                                  "/assets/images/user.png";
                              }}
                            />
                          </div>

                          <div className="assigned-form-number">
                            #{form.task_id}
                          </div>
                        </div>

                        <div className="assigned-form-body">
                          <div className="assigned-form-card-top">
                            <div className="assigned-form-heading">
                              <div className="assigned-form-club-name">
                                <i className="feather-users" />
                                <span>
                                  {form.client_name ||
                                    "Bharat Sangam Club"}
                                </span>
                              </div>

                              <h3>
                                {form.task_title ||
                                  "Untitled Form"}
                              </h3>
                            </div>

                            <span
                              className={getStatusClass(
                                form,
                              )}
                            >
                              {getStatusLabel(form)}
                            </span>
                          </div>

                          <p className="assigned-form-description">
                            {form.task_description ||
                              "No description provided for this form."}
                          </p>

                          <div className="assigned-form-meta-grid">
                            <div className="assigned-form-meta">
                              <span className="assigned-meta-icon deadline">
                                <i className="feather-calendar" />
                              </span>

                              <div>
                                <label>Deadline</label>
                                <strong>
                                  {formatDeadline(
                                    form.deadline,
                                  )}
                                </strong>
                              </div>
                            </div>

                            <div className="assigned-form-meta">
                              <span className="assigned-meta-icon fields">
                                <i className="feather-list" />
                              </span>

                              <div>
                                <label>Form Fields</label>
                                <strong>
                                  {Number(
                                    form.total_fields || 0,
                                  )}{" "}
                                  Fields
                                </strong>
                              </div>
                            </div>

                            <div className="assigned-form-meta">
                              <span className="assigned-meta-icon responses">
                                <i className="feather-send" />
                              </span>

                              <div>
                                <label>Responses</label>
                                <strong>
                                  {Number(
                                    form.submitted_responses ||
                                      0,
                                  )}
                                  /
                                  {Number(
                                    form.allowed_responses ||
                                      1,
                                  )}
                                </strong>
                              </div>
                            </div>
                          </div>

                          <div className="assigned-form-actions">
                            <div className="assigned-form-access-note">
                              <i className="feather-lock" />
                              <span>
                                Assigned to your member account
                              </span>
                            </div>

                            <button
                              type="button"
                              className={`assigned-form-open-btn ${
                                Number(form.can_submit) !== 1
                                  ? "view-mode"
                                  : ""
                              }`}
                              onClick={() =>
                                handleOpenForm(form)
                              }
                            >
                              {Number(form.can_submit) === 1
                                ? form.submission_status ===
                                  "partially_completed"
                                  ? "Continue Form"
                                  : "Open Form"
                                : "View Response"}

                              <i className="feather-arrow-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {pagination.totalPages > 1 && (
                    <div className="assigned-forms-pagination">
                      <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() =>
                          setPage((current) =>
                            Math.max(current - 1, 1),
                          )
                        }
                      >
                        <i className="feather-chevron-left" />
                        Previous
                      </button>

                      <span>
                        Page {pagination.page} of{" "}
                        {pagination.totalPages}
                      </span>

                      <button
                        type="button"
                        disabled={
                          page >= pagination.totalPages
                        }
                        onClick={() =>
                          setPage((current) => current + 1)
                        }
                      >
                        Next
                        <i className="feather-chevron-right" />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="assigned-forms-empty">
                  <div className="assigned-empty-icon">
                    <i className="feather-file-text" />
                  </div>

                  <h4>No Assigned Forms Found</h4>

                  <p>
                    Forms assigned through your club
                    membership, group, or designation will
                    appear here.
                  </p>

                  {(searchTerm ||
                    taskStatus !== "all" ||
                    submissionStatus !== "all") && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        setTaskStatus("all");
                        setSubmissionStatus("all");
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Popupchat />
      <Appfooter />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .assigned-forms-main {
          width: 100%;
          min-height: 100vh;
          padding-top: 58px;
          padding-left: 285px;
          padding-right: ${userId > 0 ? "315px" : "20px"};
          overflow-x: hidden;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
        }

        .assigned-forms-middle,
        .assigned-forms-left {
          width: 100%;
          max-width: 100%;
        }

        .assigned-forms-wrapper {
          width: 100%;
          max-width: 900px;
          margin-left: 22px;
          margin-top: 28px;
          padding-bottom: 40px;
        }

        /* ==========================
           HEADER
        ========================== */

        .assigned-forms-header {
          margin-bottom: 18px;
          padding: 14px 16px;
          border: 1px solid #e2eaf6;
          border-radius: 16px;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f7faff 58%,
              #f1f6ff 100%
            );
          box-shadow: 0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .assigned-forms-header-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .assigned-forms-heading-wrap {
          min-width: 0;
          flex: 1;
        }

        .assigned-forms-badge {
          display: none;
        }

        .assigned-forms-title {
          margin: 0 0 4px;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          line-height: 1.25;
          letter-spacing: -0.01em;
        }

        .assigned-forms-subtitle {
          max-width: 620px;
          margin: 0;
          color: #8a93a3;
          font-size: 13px;
          line-height: 1.5;
        }

        .assigned-forms-count-box {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 26px;
          padding: 0 9px;
          flex-shrink: 0;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.78);
          white-space: nowrap;
        }

        .assigned-forms-count-box strong,
        .assigned-forms-count-box span {
          display: inline;
          margin: 0;
          color: #5f7fae;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        /* ==========================
           SUMMARY
        ========================== */

        .assigned-forms-summary {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 10px;
        }

        .assigned-summary-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 30px;
          padding: 0 9px;
          border: 1px solid #e3eaf4;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.72);
        }

        .assigned-summary-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          font-size: 10px;
        }

        .assigned-summary-icon.active {
          color: #2563eb;
          background: #eff6ff;
        }

        .assigned-summary-icon.pending {
          color: #d97706;
          background: #fffbeb;
        }

        .assigned-summary-icon.submitted {
          color: #059669;
          background: #ecfdf5;
        }

        .assigned-summary-item strong,
        .assigned-summary-item span {
          display: inline;
          margin: 0;
          font-size: 10.5px;
          line-height: 1;
        }

        .assigned-summary-item strong {
          color: #475569;
          font-weight: 700;
        }

        .assigned-summary-item span {
          color: #8a93a3;
          font-weight: 600;
        }

        /* ==========================
           FILTERS
        ========================== */

        .assigned-forms-filter-row {
          display: grid;
          grid-template-columns: ${userId > 0 ? "150px 160px minmax(0, 1fr)" : "160px minmax(0, 1fr)"};
          gap: 10px;
          margin-top: 10px;
          align-items: center;
        }

        .assigned-form-select {
          position: relative;
          height: 40px;
        }

        .assigned-form-select select {
          width: 100%;
          height: 40px;
          padding: 0 34px 0 12px;
          appearance: none;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          outline: none;
          background: rgba(255, 255, 255, 0.92);
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        .assigned-form-select select:focus {
          border-color: #2F6FE4;
          box-shadow: 0 0 0 3px rgba(47, 111, 228, 0.10);
        }

        .assigned-form-select > i {
          position: absolute;
          top: 50%;
          right: 12px;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 14px;
          pointer-events: none;
        }

        .assigned-form-search {
          position: relative;
          height: 40px;
          display: flex;
          align-items: center;
        }

        .assigned-form-search > i {
          position: absolute;
          left: 14px;
          top: 50%;
          z-index: 1;
          transform: translateY(-50%);
          color: #9aa4b2;
          font-size: 14px;
          pointer-events: none;
        }

        .assigned-form-search input {
          width: 100%;
          height: 40px;
          padding: 0 34px 0 36px;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          outline: none;
          background: rgba(255, 255, 255, 0.92);
          color: #1f2937;
          font-size: 13px;
          box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.05);
        }

        .assigned-form-search input::placeholder {
          color: #9aa4b2;
        }

        .assigned-form-search input:focus {
          border-color: #2F6FE4;
          box-shadow: 0 0 0 3px rgba(47, 111, 228, 0.10);
        }

        .assigned-form-search button {
          position: absolute;
          right: 10px;
          top: 50%;
          width: 18px;
          height: 18px;
          padding: 0;
          border: 0;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transform: translateY(-50%);
          color: #64748b;
          background: #eef1f6;
          font-size: 10px;
        }

        /* ==========================
           SECTIONS
        ========================== */

        .public-forms-section {
          margin-bottom: 18px;
        }

        .assigned-member-section {
          margin: 18px 0;
        }

        .forms-section-heading {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          padding: 14px 16px;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.045);
        }

        .forms-section-kicker {
          display: none;
        }

        .forms-section-heading h3 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.3;
        }

        .forms-section-heading p {
          margin: 4px 0 0;
          color: #8a93a3;
          font-size: 12px;
          line-height: 1.5;
        }

        .forms-section-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 26px;
          min-height: 26px;
          padding: 0 8px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          color: #5f7fae;
          background: #f8fbff;
          font-size: 11px;
          font-weight: 700;
        }

        /* ==========================
           FORM LIST / CARD
        ========================== */

        .assigned-forms-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .assigned-form-card {
          position: relative;
          display: grid;
          grid-template-columns: 66px minmax(0, 1fr);
          align-items: stretch;
          width: 100%;
          overflow: hidden;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
          transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .assigned-form-card:hover {
          transform: translateY(-2px);
          border-color: #c9d9f8;
          box-shadow: 0 12px 28px rgba(10, 35, 84, 0.10);
        }

        .assigned-form-card::before {
          display: none;
        }

        .assigned-form-side {
          width: 66px;
          min-width: 66px;
          padding: 14px 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          background: #f8fafc;
          border-right: 1px solid #edf1f6;
        }

        .public-form-card .assigned-form-side {
          background: #f5faff;
        }

        .assigned-form-club-logo {
          width: 42px;
          height: 42px;
          overflow: hidden;
          border: 2px solid #ffffff;
          border-radius: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
        }

        .assigned-form-club-logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .assigned-form-number {
          margin-top: 7px;
          color: #94a3b8;
          font-size: 9.5px;
          font-weight: 700;
        }

        .assigned-form-body {
          min-width: 0;
          padding: 14px;
        }

        .assigned-form-card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .assigned-form-heading {
          min-width: 0;
        }

        .assigned-form-club-name {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-bottom: 5px;
          color: #2F6FE4;
          font-size: 10.5px;
          font-weight: 600;
        }

        .assigned-form-heading h3 {
          margin: 0;
          color: #0f1b34;
          font-family: 'Fraunces', serif;
          font-size: 17px;
          font-weight: 600;
          line-height: 1.35;
        }

        .assigned-form-status,
        .public-form-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          min-height: 24px;
          padding: 0 8px;
          border-radius: 999px;
          font-size: 9.5px;
          font-weight: 700;
        }

        .assigned-form-status.pending {
          color: #b45309;
          background: #fff7ed;
          border: 1px solid #fed7aa;
        }

        .assigned-form-status.submitted {
          color: #047857;
          background: #ecfdf5;
          border: 1px solid #d1fae5;
        }

        .assigned-form-status.progress {
          color: #1d4ed8;
          background: #eff6ff;
          border: 1px solid #dbeafe;
        }

        .assigned-form-status.closed {
          color: #64748b;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
        }

        .public-form-badge {
          color: #0369a1;
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          text-transform: none;
        }

        .assigned-form-description {
          margin: 8px 0 0;
          color: #7c8494;
          font-size: 12.5px;
          line-height: 1.5;
        }

        .assigned-form-meta-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          margin-top: 12px;
        }

        .assigned-form-meta {
          min-width: 0;
          padding: 9px;
          display: flex;
          align-items: center;
          gap: 8px;
          border: 1px solid #edf1f6;
          border-radius: 10px;
          background: #f8fafc;
        }

        .assigned-meta-icon {
          width: 30px;
          height: 30px;
          min-width: 30px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }

        .assigned-meta-icon.deadline {
          color: #dc2626;
          background: #fef2f2;
        }

        .assigned-meta-icon.fields {
          color: #7c3aed;
          background: #f5f3ff;
        }

        .assigned-meta-icon.responses {
          color: #2F6FE4;
          background: #eef4ff;
        }

        .assigned-form-meta label {
          display: block;
          margin-bottom: 2px;
          color: #94a3b8;
          font-size: 9.5px;
          font-weight: 600;
        }

        .assigned-form-meta strong {
          display: block;
          overflow: hidden;
          color: #475569;
          font-size: 10.5px;
          font-weight: 700;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .assigned-form-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #edf1f6;
        }

        .assigned-form-access-note {
          display: flex;
          align-items: center;
          gap: 5px;
          color: #94a3b8;
          font-size: 10.5px;
        }

        .public-access-note {
          color: #5f7fae;
        }

        .assigned-form-open-btn {
          min-width: 125px;
          min-height: 34px;
          padding: 0 12px;
          border: 1px solid #2F6FE4;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          color: #ffffff;
          background: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .assigned-form-open-btn:hover {
          background: #1f57c7;
          transform: translateY(-1px);
        }

        .assigned-form-open-btn.view-mode {
          color: #475569;
          background: #f8fafc;
          border-color: #dce3ec;
        }

        /* ==========================
           LOADING / EMPTY / ERROR
        ========================== */

        .assigned-forms-loader,
        .assigned-forms-empty,
        .assigned-forms-error {
          min-height: 200px;
          padding: 28px 22px;
          border: 1px dashed #dbe2ec;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: #ffffff;
          box-shadow: none;
        }

        .public-forms-state {
          margin-bottom: 18px;
        }

        .assigned-forms-loader p {
          margin: 10px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
        }

        .assigned-empty-icon {
          width: 44px;
          height: 44px;
          margin: 0 auto 11px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          background: #eef4fb;
          font-size: 20px;
        }

        .assigned-empty-icon.error {
          color: #dc2626;
          background: #fff1f2;
        }

        .assigned-forms-empty h4,
        .assigned-forms-error h4 {
          margin: 0;
          color: #475569;
          font-size: 14px;
          font-weight: 700;
        }

        .assigned-forms-empty p,
        .assigned-forms-error p {
          max-width: 470px;
          margin: 6px auto 0;
          color: #94a3b8;
          font-size: 11.5px;
          line-height: 1.55;
        }

        .assigned-forms-empty button {
          min-height: 34px;
          margin-top: 12px;
          padding: 0 12px;
          border: 1px solid #2F6FE4;
          border-radius: 10px;
          color: #ffffff;
          background: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
        }

        /* ==========================
           GUEST LOGIN
        ========================== */

        .guest-assigned-login-card {
          margin: 18px 0;
          padding: 14px 16px;
          border: 1px solid #e2eaf6;
          border-radius: 16px;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f7faff 100%
            );
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
        }

        .guest-assigned-login-content {
          display: grid;
          grid-template-columns: 40px minmax(0, 1fr) auto;
          align-items: center;
          gap: 10px;
        }

        .guest-assigned-login-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          background: #eef4fb;
          font-size: 17px;
        }

        .guest-assigned-login-content strong {
          display: block;
          color: #0f1b34;
          font-size: 12.5px;
          font-weight: 700;
        }

        .guest-assigned-login-content p {
          margin: 3px 0 0;
          color: #8a93a3;
          font-size: 10.5px;
          line-height: 1.45;
        }

        .guest-assigned-login-content button {
          min-height: 34px;
          padding: 0 12px;
          border: 1px solid #2F6FE4;
          border-radius: 10px;
          color: #ffffff;
          background: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
        }

        /* ==========================
           PAGINATION
        ========================== */

        .assigned-forms-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 16px;
        }

        .assigned-forms-pagination button {
          min-height: 32px;
          padding: 0 10px;
          border: 1px solid #dce7f7;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          color: #2F6FE4;
          background: #ffffff;
          font-size: 10.5px;
          font-weight: 700;
        }

        .assigned-forms-pagination button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .assigned-forms-pagination span {
          color: #8a93a3;
          font-size: 10.5px;
        }

        /* ==========================
           RESPONSIVE
        ========================== */

        @media (max-width: 1400px) {
          .assigned-forms-main {
            padding-right: ${userId > 0 ? "290px" : "20px"};
          }
        }

        @media (max-width: 1200px) {
          .assigned-forms-main {
            padding-left: 20px;
            padding-right: 20px;
          }

          .assigned-forms-wrapper {
            margin-left: 0;
            max-width: 100%;
          }
        }

        @media (max-width: 991px) {
          .assigned-forms-filter-row {
            grid-template-columns: 1fr;
          }

          .assigned-form-meta-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 700px) {
          .assigned-form-card {
            display: block;
          }

          .assigned-form-side {
            width: 100%;
            min-width: 100%;
            flex-direction: row;
            justify-content: flex-start;
            gap: 9px;
            padding: 10px 13px;
            border-right: 0;
            border-bottom: 1px solid #edf1f6;
          }

          .assigned-form-number {
            margin-top: 0;
          }
        }

        @media (max-width: 768px) {
          .assigned-forms-main {
            padding-top: 16px;
            padding-left: 12px;
            padding-right: 12px;
          }

          .assigned-forms-middle,
          .assigned-forms-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .assigned-forms-wrapper {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding-bottom: 76px;
          }

          .assigned-forms-header {
            padding: 12px 13px;
            border-radius: 14px;
          }

          .assigned-forms-title {
            font-size: 21px;
          }

          .forms-section-heading {
            padding: 13px;
            border-radius: 14px;
          }
        }

        @media (max-width: 575px) {
          .assigned-forms-header-top {
            flex-direction: column;
          }

          .assigned-forms-summary {
            gap: 5px;
          }

          .assigned-summary-item {
            flex: 1;
            justify-content: center;
          }

          .assigned-form-body {
            padding: 13px;
          }

          .assigned-form-card-top,
          .assigned-form-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .assigned-form-status,
          .public-form-badge {
            align-self: flex-start;
          }

          .assigned-form-open-btn {
            width: 100%;
          }

          .guest-assigned-login-content {
            grid-template-columns: 40px minmax(0, 1fr);
          }

          .guest-assigned-login-content button {
            grid-column: 1 / -1;
            width: 100%;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default AssignedForms;
